import { z } from 'zod';

import {
  WORD_CHANT_FAST_BEAT_FRAMES,
  WORD_CHANT_FPS,
  WORD_CHANT_INTERLUDE_FRAMES,
  WORD_CHANT_INTRO_FRAMES,
  WORD_CHANT_MAX_WORD_COUNT,
  WORD_CHANT_MIN_WORD_COUNT,
  WORD_CHANT_NORMAL_BEAT_FRAMES,
  WORD_CHANT_OUTRO_FRAMES,
  calculateWordChantDurationFrames,
  wordSceneFramesForRound,
} from '../lib/word-chant-contract';
import { wordChantStyleSchema } from '../lib/word-chant-style-catalog';

const wordChantImageAssetSchema = z
  .object({
    assetId: z.string().trim().min(1),
    type: z.literal('image'),
    role: z.enum(['illustration', 'photo']),
    wordId: z.string().trim().min(1),
    bucket: z.string().trim().min(1),
    s3Key: z.string().trim().min(1),
    url: z.string().url(),
    contentType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .passthrough();

const wordChantAudioAssetSchema = z
  .object({
    assetId: z.string().trim().min(1),
    type: z.literal('audio'),
    role: z.literal('word-chant'),
    wordId: z.string().trim().min(1),
    round: z.enum(['normal', 'fast']),
    bucket: z.string().trim().min(1),
    s3Key: z.string().trim().min(1),
    url: z.string().url(),
    durationSec: z.number().positive(),
    durationFrames: z.number().int().positive(),
  })
  .passthrough();

const wordChantBackgroundMusicAssetSchema = z
  .object({
    assetId: z.literal('word-chant-background-music'),
    type: z.literal('audio'),
    role: z.literal('background-music'),
    bucket: z.string().trim().min(1),
    s3Key: z.string().trim().min(1),
    url: z.string().url(),
    contentType: z.literal('audio/mpeg'),
    durationSec: z.number().positive(),
    durationFrames: z.number().int().positive(),
    volume: z.number().min(0).max(1),
  })
  .passthrough();

const wordChantGuideVoiceAssetSchema = z
  .object({
    assetId: z.string().trim().min(1),
    type: z.literal('audio'),
    role: z.literal('guide-voice'),
    cueRole: z.enum(['ready-set-go', 'faster-now', 'good-job']),
    resourceId: z.number().int().positive(),
    bucket: z.literal('crabs-resources'),
    s3Key: z.string().trim().min(1),
    url: z.string().url(),
    contentType: z.enum(['audio/mpeg', 'audio/wav']),
    durationSec: z.number().positive(),
    durationFrames: z.number().int().positive(),
    volume: z.number().min(0).max(1),
  })
  .passthrough();

const wordChantAudioTrackItemSchema = z
  .object({
    itemId: z.string().trim().min(1),
    assetId: z.string().trim().min(1),
    fromFrame: z.number().int().nonnegative(),
    durationInFrames: z.number().int().positive(),
    volume: z.number().min(0).max(1),
  })
  .strict();

const wordChantDuckingRegionSchema = z
  .object({
    startFrame: z.number().int().nonnegative(),
    endFrame: z.number().int().positive(),
    gain: z.number().positive().max(1),
    attackFrames: z.number().int().nonnegative(),
    releaseFrames: z.number().int().nonnegative(),
  })
  .strict();

const wordChantBackgroundTrackItemSchema = wordChantAudioTrackItemSchema.extend(
  {
    duckingRegions: z.array(wordChantDuckingRegionSchema),
  },
);

const wordChantAudioTracksSchema = z.tuple([
  z
    .object({
      trackId: z.literal('background-music'),
      role: z.literal('background-music'),
      items: z.array(wordChantBackgroundTrackItemSchema).max(1),
    })
    .strict(),
  z
    .object({
      trackId: z.literal('guide-voice'),
      role: z.literal('guide-voice'),
      items: z.array(wordChantAudioTrackItemSchema).length(3),
    })
    .strict(),
  z
    .object({
      trackId: z.literal('word-tts'),
      role: z.literal('word-tts'),
      items: z.array(wordChantAudioTrackItemSchema),
    })
    .strict(),
]);

const wordChantRenderWordSchema = z
  .object({
    wordId: z.string().trim().min(1),
    text: z.string().trim().min(1),
    order: z.number().int().min(1).max(WORD_CHANT_MAX_WORD_COUNT),
    illustrationAssetId: z.string().trim().min(1),
    photoAssetId: z.string().trim().min(1),
    audioAssetIds: z
      .object({
        normal: z.string().trim().min(1),
        fast: z.string().trim().min(1),
      })
      .strict(),
  })
  .strict();

const wordChantStaticRenderSceneSchema = z
  .object({
    sceneId: z.string().trim().min(1),
    kind: z.enum(['intro', 'interlude', 'outro']),
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().positive(),
    durationFrames: z.number().int().positive(),
    layoutVariant: z.string().trim().min(1),
  })
  .passthrough();

const wordChantWordRenderSceneSchema = z
  .object({
    sceneId: z.string().trim().min(1),
    kind: z.literal('word'),
    round: z.enum(['normal', 'fast']),
    wordId: z.string().trim().min(1),
    wordIndex: z.number().int().positive(),
    activeWordIndex: z.number().int().min(0),
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().positive(),
    durationFrames: z.number().int().positive(),
    cueFrames: z.tuple([
      z.number().int().min(0),
      z.number().int().min(0),
      z.number().int().min(0),
      z.number().int().min(0),
    ]),
    flipFrame: z.number().int().min(0),
    teacherEndFrame: z.number().int().min(0),
    layoutVariant: z.string().trim().min(1),
    visualVariant: z.literal('illustration-to-photo'),
    illustrationAssetId: z.string().trim().min(1),
    photoAssetId: z.string().trim().min(1),
    audioAssetId: z.string().trim().min(1),
  })
  .passthrough();

export const wordChantRenderSpecSchema = z
  .object({
    version: z.literal('word_chant.render_spec.v1'),
    templateId: z.literal('word-chant-v1'),
    rendererVersion: z.literal('word-chant-remotion-v1'),
    title: z.string().trim().min(1),
    canvas: z
      .object({
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(WORD_CHANT_FPS),
      })
      .strict(),
    durationFrames: z.number().int().positive(),
    durationSec: z.number().positive(),
    timingMode: z.literal('fixed_beat_atomic_audio'),
    wordCount: z
      .number()
      .int()
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    thumbnailLayoutVariant: z.string().trim().min(1),
    words: z
      .array(wordChantRenderWordSchema)
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    scenes: z
      .array(
        z.discriminatedUnion('kind', [
          wordChantStaticRenderSceneSchema,
          wordChantWordRenderSceneSchema,
        ]),
      )
      .min(9),
    assets: z.array(
      z.discriminatedUnion('type', [
        wordChantImageAssetSchema,
        wordChantAudioAssetSchema,
      ]),
    ),
  })
  .passthrough()
  .superRefine((spec, context) => {
    if (
      spec.durationSec !== spec.durationFrames / WORD_CHANT_FPS ||
      spec.durationFrames !== calculateWordChantDurationFrames(spec.wordCount)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationFrames'],
        message: 'Word Chant duration must be derived from the 24fps topology',
      });
    }
    if (
      spec.words.length !== spec.wordCount ||
      spec.scenes.length !== spec.wordCount * 2 + 3
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['wordCount'],
        message: 'Word Chant collection counts do not match wordCount',
      });
    }

    const wordIds = spec.words.map((word) => word.wordId);
    const assetIdList = spec.assets.map((asset) => asset.assetId);
    const assetIds = new Set(assetIdList);
    const assetsById = new Map(
      spec.assets.map((asset) => [asset.assetId, asset]),
    );
    if (
      new Set(wordIds).size !== wordIds.length ||
      spec.words.some((word, index) => word.order !== index + 1)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['words'],
        message: 'Word Chant words must be unique and ordered',
      });
    }
    if (assetIds.size !== assetIdList.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets'],
        message: 'Word Chant asset IDs must be unique',
      });
    }
    if (
      spec.assets.some(
        (asset) =>
          asset.type === 'audio' &&
          (asset.durationFrames !== wordSceneFramesForRound(asset.round) ||
            Math.abs(
              asset.durationSec - asset.durationFrames / WORD_CHANT_FPS,
            ) > 1e-9),
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets'],
        message:
          'Word Chant audio duration must match its round at the 24fps frame grid',
      });
    }
    if (
      spec.words.some(
        (word) =>
          !assetIds.has(word.illustrationAssetId) ||
          !assetIds.has(word.photoAssetId) ||
          !assetIds.has(word.audioAssetIds.normal) ||
          !assetIds.has(word.audioAssetIds.fast),
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets'],
        message: 'Word Chant word asset references must exist',
      });
    }
    if (
      spec.words.some((word) => {
        const illustration = assetsById.get(word.illustrationAssetId);
        const photo = assetsById.get(word.photoAssetId);
        const normalAudio = assetsById.get(word.audioAssetIds.normal);
        const fastAudio = assetsById.get(word.audioAssetIds.fast);
        return (
          illustration?.type !== 'image' ||
          illustration.role !== 'illustration' ||
          illustration.wordId !== word.wordId ||
          photo?.type !== 'image' ||
          photo.role !== 'photo' ||
          photo.wordId !== word.wordId ||
          normalAudio?.type !== 'audio' ||
          normalAudio.round !== 'normal' ||
          normalAudio.wordId !== word.wordId ||
          fastAudio?.type !== 'audio' ||
          fastAudio.round !== 'fast' ||
          fastAudio.wordId !== word.wordId
        );
      })
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets'],
        message: 'Word Chant assets must match their word, role, and round',
      });
    }

    const expectedTopology = [
      'intro',
      ...Array<string>(spec.wordCount).fill('normal-word'),
      'interlude',
      ...Array<string>(spec.wordCount).fill('fast-word'),
      'outro',
    ];
    const actualTopology = spec.scenes.map((scene) =>
      scene.kind === 'word' ? `${scene.round}-word` : scene.kind,
    );
    if (
      actualTopology.some((kind, index) => kind !== expectedTopology[index])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message:
          'Word Chant scenes must be intro, normal words, interlude, fast words, and outro',
      });
    }

    let cursor = 0;
    const sceneIds = new Set<string>();
    for (const [sceneIndex, scene] of spec.scenes.entries()) {
      sceneIds.add(scene.sceneId);
      if (
        scene.startFrame !== cursor ||
        scene.endFrame - scene.startFrame !== scene.durationFrames
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes'],
          message: 'Word Chant scenes must be contiguous',
        });
        break;
      }
      if (
        scene.kind === 'word' &&
        (scene.durationFrames !== wordSceneFramesForRound(scene.round) ||
          !assetIds.has(scene.illustrationAssetId) ||
          !assetIds.has(scene.photoAssetId) ||
          !assetIds.has(scene.audioAssetId))
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes'],
          message: 'Word Chant word scene timing or assets are invalid',
        });
      }
      if (scene.kind === 'word') {
        const word = spec.words[scene.activeWordIndex];
        const beatFrames =
          scene.round === 'normal'
            ? WORD_CHANT_NORMAL_BEAT_FRAMES
            : WORD_CHANT_FAST_BEAT_FRAMES;
        const expectedCues = scene.cueFrames.map(
          (_, index) => scene.startFrame + beatFrames * index,
        );
        const expectedFlipFrame = Math.min(
          scene.cueFrames[2] - 1,
          Math.max(
            scene.cueFrames[1] + 1,
            Math.floor((scene.teacherEndFrame + scene.cueFrames[2]) / 2),
          ),
        );
        if (
          !word ||
          scene.wordIndex !== scene.activeWordIndex + 1 ||
          scene.wordId !== word.wordId ||
          scene.illustrationAssetId !== word.illustrationAssetId ||
          scene.photoAssetId !== word.photoAssetId ||
          scene.audioAssetId !== word.audioAssetIds[scene.round] ||
          scene.cueFrames.some(
            (cueFrame, index) => cueFrame !== expectedCues[index],
          ) ||
          !(
            scene.cueFrames[1] <= scene.teacherEndFrame &&
            scene.teacherEndFrame <= scene.cueFrames[2]
          ) ||
          !(
            scene.cueFrames[1] < scene.flipFrame &&
            scene.flipFrame < scene.cueFrames[2]
          ) ||
          scene.flipFrame !== expectedFlipFrame
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', sceneIndex],
            message: 'Word Chant word scene bindings or cue frames are invalid',
          });
        }
      } else {
        const expectedDuration =
          scene.kind === 'intro'
            ? WORD_CHANT_INTRO_FRAMES
            : scene.kind === 'interlude'
              ? WORD_CHANT_INTERLUDE_FRAMES
              : WORD_CHANT_OUTRO_FRAMES;
        if (scene.durationFrames !== expectedDuration) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', sceneIndex, 'durationFrames'],
            message: `Word Chant ${scene.kind} duration is fixed`,
          });
        }
      }
      cursor = scene.endFrame;
    }
    if (sceneIds.size !== spec.scenes.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'Word Chant scene IDs must be unique',
      });
    }
    if (cursor !== spec.durationFrames) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message:
          'Word Chant scene duration must equal the composition duration',
      });
    }
  });

export const wordChantRenderSpecV2Schema = z
  .object({
    version: z.literal('word_chant.render_spec.v2'),
    templateId: z.literal('word-chant-v1'),
    rendererVersion: z.literal('word-chant-remotion-v2'),
    title: z.string().trim().min(1),
    style: wordChantStyleSchema,
    canvas: z
      .object({
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(WORD_CHANT_FPS),
      })
      .strict(),
    durationFrames: z.number().int().positive(),
    durationSec: z.number().positive(),
    timingMode: z.literal('fixed_beat_atomic_audio'),
    wordCount: z
      .number()
      .int()
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    thumbnailLayoutVariant: z.string().trim().min(1),
    words: z
      .array(wordChantRenderWordSchema)
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    scenes: z
      .array(
        z.discriminatedUnion('kind', [
          wordChantStaticRenderSceneSchema,
          wordChantWordRenderSceneSchema,
        ]),
      )
      .min(9),
    assets: z.array(
      z.discriminatedUnion('type', [
        wordChantImageAssetSchema,
        wordChantAudioAssetSchema,
      ]),
    ),
  })
  .passthrough()
  .superRefine((spec, context) => {
    if (spec.title !== spec.style.displayTitle) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['title'],
        message: 'title must match style.displayTitle',
      });
    }

    const v1Result = wordChantRenderSpecSchema.safeParse({
      ...spec,
      version: 'word_chant.render_spec.v1',
      rendererVersion: 'word-chant-remotion-v1',
    });
    if (!v1Result.success) {
      for (const issue of v1Result.error.issues) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: issue.path,
          message: issue.message,
        });
      }
    }
  });

const wordChantAdaptiveWordRenderSceneSchema =
  wordChantWordRenderSceneSchema.extend({
    beatFrames: z.number().int().positive(),
    handoffFrames: z.number().int().nonnegative().default(0),
  });

const wordChantAdaptiveRenderSpecBaseSchema = z
  .object({
    templateId: z.literal('word-chant-v1'),
    title: z.string().trim().min(1),
    style: wordChantStyleSchema,
    canvas: z
      .object({
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(WORD_CHANT_FPS),
      })
      .strict(),
    durationFrames: z.number().int().positive(),
    durationSec: z.number().positive(),
    timingMode: z.literal('adaptive_beat_atomic_audio'),
    wordCount: z
      .number()
      .int()
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    thumbnailLayoutVariant: z.string().trim().min(1),
    words: z
      .array(wordChantRenderWordSchema)
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    scenes: z
      .array(
        z.discriminatedUnion('kind', [
          wordChantStaticRenderSceneSchema,
          wordChantAdaptiveWordRenderSceneSchema,
        ]),
      )
      .min(9),
    assets: z.array(
      z.union([
        wordChantImageAssetSchema,
        wordChantAudioAssetSchema,
        wordChantBackgroundMusicAssetSchema,
      ]),
    ),
  })
  .passthrough();

type WordChantAdaptiveRenderSpecValidation = z.infer<
  typeof wordChantAdaptiveRenderSpecBaseSchema
> & {
  backgroundMusicAssetId?: 'word-chant-background-music';
};

function validateAdaptiveRenderSpec(
  spec: WordChantAdaptiveRenderSpecValidation,
  context: z.RefinementCtx,
) {
  if (
    spec.title !== spec.style.displayTitle ||
    spec.durationSec !== spec.durationFrames / WORD_CHANT_FPS ||
    spec.words.length !== spec.wordCount ||
    spec.scenes.length !== spec.wordCount * 2 + 3
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['durationFrames'],
      message: 'Adaptive Word Chant identity or collection counts are invalid',
    });
  }
  const backgroundMusicAssets = spec.assets.filter(
    (asset) => asset.role === 'background-music',
  );
  const bgm = spec.backgroundMusicAssetId
    ? spec.assets.find((asset) => asset.assetId === spec.backgroundMusicAssetId)
    : undefined;
  if (spec.backgroundMusicAssetId === undefined) {
    if (backgroundMusicAssets.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets'],
        message: 'Background music asset must not be left unbound',
      });
    }
  } else if (
    backgroundMusicAssets.length !== 1 ||
    bgm?.role !== 'background-music' ||
    bgm.durationFrames !== spec.durationFrames ||
    Math.abs(bgm.durationSec - spec.durationSec) > 1e-9
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['backgroundMusicAssetId'],
      message: 'Background music must cover the adaptive timeline exactly',
    });
  }
  const assetIds = new Set(spec.assets.map((asset) => asset.assetId));
  const expectedTopology = [
    'intro',
    ...Array<string>(spec.wordCount).fill('normal-word'),
    'interlude',
    ...Array<string>(spec.wordCount).fill('fast-word'),
    'outro',
  ];
  let cursor = 0;
  spec.scenes.forEach((scene, sceneIndex) => {
    const topology = scene.kind === 'word' ? `${scene.round}-word` : scene.kind;
    if (
      topology !== expectedTopology[sceneIndex] ||
      scene.startFrame !== cursor ||
      scene.endFrame - scene.startFrame !== scene.durationFrames
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes', sceneIndex],
        message: 'Adaptive Word Chant scenes must be contiguous and ordered',
      });
    }
    if (scene.kind === 'word') {
      const expectedCues = [
        scene.startFrame,
        scene.startFrame + scene.beatFrames,
        scene.startFrame + scene.beatFrames * 2 + scene.handoffFrames,
        scene.startFrame + scene.beatFrames * 3 + scene.handoffFrames,
      ];
      const expectedFlipFrame = Math.min(
        scene.cueFrames[2] - 1,
        Math.max(
          scene.cueFrames[1] + 1,
          Math.floor((scene.teacherEndFrame + scene.cueFrames[2]) / 2),
        ),
      );
      if (
        scene.durationFrames !== scene.beatFrames * 4 + scene.handoffFrames ||
        scene.cueFrames.some(
          (cueFrame, index) => cueFrame !== expectedCues[index],
        ) ||
        scene.flipFrame !== expectedFlipFrame ||
        !assetIds.has(scene.illustrationAssetId) ||
        !assetIds.has(scene.photoAssetId) ||
        !assetIds.has(scene.audioAssetId)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes', sceneIndex],
          message: 'Adaptive Word Chant word timing or assets are invalid',
        });
      }
    } else {
      const expectedDuration =
        scene.kind === 'intro'
          ? WORD_CHANT_INTRO_FRAMES
          : scene.kind === 'interlude'
            ? WORD_CHANT_INTERLUDE_FRAMES
            : WORD_CHANT_OUTRO_FRAMES;
      if (scene.durationFrames !== expectedDuration) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes', sceneIndex],
          message: `Word Chant ${scene.kind} duration is fixed`,
        });
      }
    }
    cursor = scene.endFrame;
  });
  if (cursor !== spec.durationFrames) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['scenes'],
      message: 'Adaptive Word Chant scenes must cover durationFrames',
    });
  }
}

export const wordChantRenderSpecV3Schema = wordChantAdaptiveRenderSpecBaseSchema
  .extend({
    version: z.literal('word_chant.render_spec.v3'),
    rendererVersion: z.literal('word-chant-remotion-v3'),
    backgroundMusicAssetId: z.literal('word-chant-background-music'),
  })
  .superRefine(validateAdaptiveRenderSpec);

export const wordChantRenderSpecV4Schema = wordChantAdaptiveRenderSpecBaseSchema
  .extend({
    version: z.literal('word_chant.render_spec.v4'),
    rendererVersion: z.literal('word-chant-remotion-v4'),
    backgroundMusicAssetId: z.literal('word-chant-background-music').optional(),
  })
  .superRefine(validateAdaptiveRenderSpec);

export const wordChantRenderSpecV5Schema = wordChantAdaptiveRenderSpecBaseSchema
  .extend({
    version: z.literal('word_chant.render_spec.v5'),
    rendererVersion: z.literal('word-chant-remotion-v5'),
    backgroundMusicAssetId: z.literal('word-chant-background-music').optional(),
  })
  .superRefine(validateAdaptiveRenderSpec);

const WORD_CHANT_GUIDE_CUE_ROLES = [
  'ready-set-go',
  'faster-now',
  'good-job',
] as const;
const WORD_CHANT_GUIDE_CUE_DURATIONS_V6 = [138, 125, 62] as const;
const WORD_CHANT_GUIDE_CUE_DURATIONS_V7 = [168, 132, 72] as const;

const wordChantAudioTrackRenderSpecBaseSchema =
  wordChantAdaptiveRenderSpecBaseSchema.extend({
    backgroundMusicAssetId: z.literal('word-chant-background-music').optional(),
    assets: z.array(
      z.union([
        wordChantImageAssetSchema,
        wordChantAudioAssetSchema,
        wordChantBackgroundMusicAssetSchema,
        wordChantGuideVoiceAssetSchema,
      ]),
    ),
    audioTracks: wordChantAudioTracksSchema,
  });

function validateAudioTrackRenderSpec(
  spec: z.infer<typeof wordChantAudioTrackRenderSpecBaseSchema>,
  context: z.RefinementCtx,
  cueDurations: readonly [number, number, number],
  readySetGoOffsetFrames: number,
) {
  validateAdaptiveRenderSpec(
    spec as unknown as WordChantAdaptiveRenderSpecValidation,
    context,
  );
  const cueAssets = spec.assets.filter((asset) => asset.role === 'guide-voice');
  if (
    cueAssets.length !== 3 ||
    cueAssets.some(
      (asset, index) =>
        asset.cueRole !== WORD_CHANT_GUIDE_CUE_ROLES[index] ||
        asset.assetId !== `word-chant-cue-${asset.cueRole}` ||
        asset.durationFrames !== cueDurations[index] ||
        Math.abs(asset.durationSec - cueDurations[index]! / WORD_CHANT_FPS) >
          1e-9,
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assets'],
      message: 'Guide voice assets must match the pinned cue contract',
    });
  }

  const intro = spec.scenes.find((scene) => scene.kind === 'intro');
  const interlude = spec.scenes.find((scene) => scene.kind === 'interlude');
  const outro = spec.scenes.find((scene) => scene.kind === 'outro');
  const guideItems = spec.audioTracks[1].items;
  const expectedStarts = [
    (intro?.startFrame ?? -readySetGoOffsetFrames) + readySetGoOffsetFrames,
    interlude?.startFrame ?? -1,
    outro?.startFrame ?? -1,
  ];
  if (
    guideItems.some(
      (item, index) =>
        item.itemId !== `guide-voice-${WORD_CHANT_GUIDE_CUE_ROLES[index]}` ||
        item.assetId !==
          `word-chant-cue-${WORD_CHANT_GUIDE_CUE_ROLES[index]}` ||
        item.fromFrame !== expectedStarts[index] ||
        item.durationInFrames !== cueDurations[index] ||
        item.volume !== cueAssets[index]?.volume,
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['audioTracks', 1],
      message: 'Guide voice track must align with visual cue scenes',
    });
  }

  const wordScenes = spec.scenes.filter((scene) => scene.kind === 'word');
  if (
    spec.audioTracks[2].items.length !== wordScenes.length ||
    spec.audioTracks[2].items.some((item, index) => {
      const scene = wordScenes[index];
      return (
        !scene ||
        item.itemId !== `word-tts-${scene.sceneId}` ||
        item.assetId !== scene.audioAssetId ||
        item.fromFrame !== scene.startFrame ||
        item.durationInFrames !== scene.durationFrames ||
        item.volume !== 1
      );
    })
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['audioTracks', 2],
      message: 'Word TTS track must mirror every word scene',
    });
  }

  const backgroundItems = spec.audioTracks[0].items;
  const bgm = spec.assets.find((asset) => asset.role === 'background-music');
  if (
    (bgm === undefined && backgroundItems.length !== 0) ||
    (bgm !== undefined &&
      (backgroundItems.length !== 1 ||
        backgroundItems[0]?.assetId !== bgm.assetId ||
        backgroundItems[0]?.fromFrame !== 0 ||
        backgroundItems[0]?.durationInFrames !== spec.durationFrames ||
        backgroundItems[0]?.volume !== bgm.volume ||
        backgroundItems[0]?.duckingRegions.length !== 3 ||
        backgroundItems[0]?.duckingRegions.some(
          (region, index) =>
            region.startFrame !== guideItems[index]?.fromFrame ||
            region.endFrame !==
              (guideItems[index]?.fromFrame ?? 0) +
                (guideItems[index]?.durationInFrames ?? 0) ||
            Math.abs(region.gain - 10 ** (-3 / 20)) > 1e-12 ||
            region.attackFrames !== 2 ||
            region.releaseFrames !== 2,
        )))
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['audioTracks', 0],
      message: 'Background track or guide ducking regions are invalid',
    });
  }

  const assetIds = new Set(spec.assets.map((asset) => asset.assetId));
  if (
    spec.audioTracks.some((track) =>
      track.items.some(
        (item) =>
          !assetIds.has(item.assetId) ||
          item.fromFrame + item.durationInFrames > spec.durationFrames,
      ),
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['audioTracks'],
      message: 'Audio track items must bind assets inside the timeline',
    });
  }
}

export const wordChantRenderSpecV6Schema =
  wordChantAudioTrackRenderSpecBaseSchema
    .extend({
      version: z.literal('word_chant.render_spec.v6'),
      rendererVersion: z.literal('word-chant-remotion-v6'),
    })
    .superRefine((spec, context) =>
      validateAudioTrackRenderSpec(
        spec,
        context,
        WORD_CHANT_GUIDE_CUE_DURATIONS_V6,
        42,
      ),
    );

export const wordChantRenderSpecV7Schema =
  wordChantAudioTrackRenderSpecBaseSchema
    .extend({
      version: z.literal('word_chant.render_spec.v7'),
      rendererVersion: z.literal('word-chant-remotion-v7'),
    })
    .superRefine((spec, context) =>
      validateAudioTrackRenderSpec(
        spec,
        context,
        WORD_CHANT_GUIDE_CUE_DURATIONS_V7,
        0,
      ),
    );

const wordChantSourceAudioAssetSchema = z
  .object({
    assetId: z.literal('word-chant-source-audio'),
    type: z.literal('audio'),
    role: z.literal('source-audio'),
    bucket: z.string().trim().min(1),
    s3Key: z.string().trim().min(1),
    url: z.string().url(),
    contentType: z.string().trim().min(1),
    durationSec: z.number().positive(),
    durationFrames: z.number().int().positive(),
    volume: z.literal(1),
  })
  .passthrough();

const wordChantRecordedRenderWordSchema = wordChantRenderWordSchema.omit({
  audioAssetIds: true,
});

const wordChantCueSegmentSchema = z
  .object({
    cueId: z.string().trim().min(1),
    text: z.string().trim().min(1),
    displayText: z.string().trim().min(1).max(200).optional(),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().positive(),
    startFrame: z.number().int().nonnegative(),
    endFrame: z.number().int().positive(),
  })
  .strict();

export const wordChantRenderSpecV8Schema = z
  .object({
    version: z.literal('word_chant.render_spec.v8'),
    templateId: z.literal('word-chant-v1'),
    rendererVersion: z.literal('word-chant-remotion-v8'),
    title: z.string().trim().min(1),
    style: wordChantStyleSchema,
    canvas: z
      .object({
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(WORD_CHANT_FPS),
      })
      .strict(),
    durationFrames: z.number().int().positive(),
    durationSec: z.number().positive(),
    timingMode: z.literal('source_audio_word_timestamps'),
    wordCount: z
      .number()
      .int()
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    thumbnailLayoutVariant: z.string().trim().min(1),
    words: z
      .array(wordChantRecordedRenderWordSchema)
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    scenes: z.array(
      z.discriminatedUnion('kind', [
        wordChantStaticRenderSceneSchema,
        wordChantWordRenderSceneSchema,
      ]),
    ),
    assets: z.array(
      z.union([wordChantImageAssetSchema, wordChantSourceAudioAssetSchema]),
    ),
    masterAudioAssetId: z.literal('word-chant-source-audio'),
    cueSegments: z.array(wordChantCueSegmentSchema),
  })
  .passthrough()
  .superRefine((spec, context) => {
    const sourceAudio = spec.assets.find(
      (asset) => asset.assetId === spec.masterAudioAssetId,
    );
    if (
      sourceAudio?.role !== 'source-audio' ||
      sourceAudio.durationFrames !== spec.durationFrames ||
      spec.durationSec !== spec.durationFrames / WORD_CHANT_FPS ||
      spec.words.length !== spec.wordCount ||
      spec.scenes.length !== spec.wordCount * 2 + 3
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['masterAudioAssetId'],
        message: 'Recorded Word Chant duration or identity is invalid',
      });
    }
    let cursor = 0;
    spec.scenes.forEach((scene, index) => {
      if (
        scene.startFrame !== cursor ||
        scene.endFrame - scene.startFrame !== scene.durationFrames
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes', index],
          message: 'Recorded Word Chant scenes must be contiguous',
        });
      }
      cursor = scene.endFrame;
    });
    if (cursor !== spec.durationFrames) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'Recorded Word Chant scenes must cover the source audio',
      });
    }
    if (
      spec.cueSegments.some(
        (cue) =>
          cue.endMs <= cue.startMs ||
          cue.endFrame <= cue.startFrame ||
          cue.endFrame > spec.durationFrames,
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cueSegments'],
        message: 'Recorded Word Chant cues must stay on the source timeline',
      });
    }
  });

export const wordChantAnyRenderSpecSchema = z.union([
  wordChantRenderSpecSchema,
  wordChantRenderSpecV2Schema,
  wordChantRenderSpecV3Schema,
  wordChantRenderSpecV4Schema,
  wordChantRenderSpecV5Schema,
  wordChantRenderSpecV6Schema,
  wordChantRenderSpecV7Schema,
  wordChantRenderSpecV8Schema,
]);

export type WordChantRenderSpec = z.infer<typeof wordChantAnyRenderSpecSchema>;
export type WordChantRenderSpecV2 = z.infer<typeof wordChantRenderSpecV2Schema>;
export type WordChantRenderSpecV3 = z.infer<typeof wordChantRenderSpecV3Schema>;
export type WordChantRenderSpecV4 = z.infer<typeof wordChantRenderSpecV4Schema>;
export type WordChantRenderSpecV5 = z.infer<typeof wordChantRenderSpecV5Schema>;
export type WordChantRenderSpecV6 = z.infer<typeof wordChantRenderSpecV6Schema>;
export type WordChantRenderSpecV7 = z.infer<typeof wordChantRenderSpecV7Schema>;
export type WordChantRenderSpecV8 = z.infer<typeof wordChantRenderSpecV8Schema>;

export interface WordChantImageRenderAsset {
  assetId: string;
  type: 'image';
  role: 'illustration' | 'photo';
  wordId: string;
  bucket: string;
  s3Key: string;
  url: string;
  contentType: 'image/png' | 'image/jpeg' | 'image/webp';
  width: number;
  height: number;
  [key: string]: unknown;
}

export interface WordChantAudioRenderAsset {
  assetId: string;
  type: 'audio';
  role: 'word-chant';
  wordId: string;
  round: 'normal' | 'fast';
  bucket: string;
  s3Key: string;
  url: string;
  durationSec: number;
  durationFrames: number;
  [key: string]: unknown;
}

export interface WordChantBackgroundMusicRenderAsset {
  assetId: 'word-chant-background-music';
  type: 'audio';
  role: 'background-music';
  bucket: string;
  s3Key: string;
  url: string;
  contentType: 'audio/mpeg';
  durationSec: number;
  durationFrames: number;
  volume: number;
  [key: string]: unknown;
}

export interface WordChantGuideVoiceRenderAsset {
  assetId: string;
  type: 'audio';
  role: 'guide-voice';
  cueRole: 'ready-set-go' | 'faster-now' | 'good-job';
  resourceId: number;
  bucket: 'crabs-resources';
  s3Key: string;
  url: string;
  contentType: 'audio/mpeg' | 'audio/wav';
  durationSec: number;
  durationFrames: number;
  volume: number;
  [key: string]: unknown;
}

export interface WordChantSourceAudioRenderAsset {
  assetId: 'word-chant-source-audio';
  type: 'audio';
  role: 'source-audio';
  bucket: string;
  s3Key: string;
  url: string;
  contentType: string;
  durationSec: number;
  durationFrames: number;
  volume: 1;
  [key: string]: unknown;
}

export type WordChantRenderAsset =
  | WordChantImageRenderAsset
  | WordChantAudioRenderAsset
  | WordChantBackgroundMusicRenderAsset
  | WordChantGuideVoiceRenderAsset
  | WordChantSourceAudioRenderAsset;
export type WordChantRenderWord = WordChantRenderSpec['words'][number];

export interface WordChantStaticRenderScene {
  sceneId: string;
  kind: 'intro' | 'interlude' | 'outro';
  startFrame: number;
  endFrame: number;
  durationFrames: number;
  layoutVariant: string;
  [key: string]: unknown;
}

export interface WordChantWordRenderScene {
  sceneId: string;
  kind: 'word';
  round: 'normal' | 'fast';
  wordId: string;
  wordIndex: number;
  activeWordIndex: number;
  startFrame: number;
  endFrame: number;
  durationFrames: number;
  beatFrames?: number;
  handoffFrames?: number;
  cueFrames: [number, number, number, number];
  flipFrame: number;
  teacherEndFrame: number;
  layoutVariant: string;
  visualVariant: 'illustration-to-photo';
  illustrationAssetId: string;
  photoAssetId: string;
  audioAssetId: string;
  [key: string]: unknown;
}

export type WordChantRenderScene =
  | WordChantStaticRenderScene
  | WordChantWordRenderScene;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function safeParseWordChantPreviewPayload(payload: unknown) {
  const record = asRecord(payload);
  const directSpec = record?.renderSpec;
  const composition = asRecord(record?.composition);
  const assets = Array.isArray(composition?.assets) ? composition.assets : [];
  const templateSpec = assets
    .map(asRecord)
    .find((asset) => asset?.type === 'remotion-template')?.spec;
  return wordChantAnyRenderSpecSchema.safeParse(directSpec ?? templateSpec);
}
